




































































import {
  Component,
  Prop,
  Model,
  Watch,
  Vue as VueDecorator,
} from "vue-property-decorator";
import {
  formatDateShort,
  formatDateWithWeekday,
  fromIsoString,
  toDatePickerFormat,
} from "@/utils/DateFormatter";
import { firestore } from "firebase/app";
import { DateTime } from "luxon";

@Component
export default class DatePickerCompoment extends VueDecorator {
  @Model("change")
  timestamp!: firebase.firestore.Timestamp | null;
  @Prop() label!: string;

  @Prop() minDate!: firebase.firestore.Timestamp | null;
  @Prop() maxDate!: firebase.firestore.Timestamp | null;
  @Prop({ default: false }) readonly!: boolean;
  @Prop({ default: false }) disabled!: boolean;
  @Prop({ type: Boolean, default: false }) hideDetails!: boolean;
  @Prop({ default: "" }) textFieldStyle!: string;
  @Prop({ default: false }) alwaysUpdateInternalDate!: boolean;
  @Prop({ default: false }) hideYear!: boolean;
  @Prop({ default: false }) keepHoursAndMinutes!: boolean;
  @Prop({ default: null })
  initialDatePickerDate!: firebase.firestore.Timestamp | null;
  @Prop({ default: false }) showWeekday!: boolean;
  @Prop({ default: false }) showClearInputOption!: boolean;

  /**
   * Vuetify field styling, passed straight through.
   *
   * Off by default so every existing usage keeps the plain underlined field. Set both where the picker
   * sits among `outlined dense` controls, or it stands a row taller than its neighbours and reads as
   * belonging to something else.
   */
  @Prop({ type: Boolean, default: false }) outlined!: boolean;
  @Prop({ type: Boolean, default: false }) dense!: boolean;
  
  @Prop({ default: () => () => true, type: Function }) 
  readonly allowedDates!: (date: string) => boolean;

  isPickerOpen = false;

  get showClearInputIcon() {
    return this.showClearInputOption && !!this.internal;
  }

  get minDateString() {
    return this.minDate ? toDatePickerFormat(this.minDate.toDate()) : "";
  }

  get maxDateString() {
    return this.maxDate ? toDatePickerFormat(this.maxDate.toDate()) : "";
  }

  internal: firebase.firestore.Timestamp | null = null;

  @Watch("timestamp", { immediate: true })
  onNewTimestamp(timestamp: firestore.Timestamp | null) {
    if (!this.internal || this.alwaysUpdateInternalDate) {
      this.internal = timestamp;
    }
  }

  setNewTimestamp(date: firestore.Timestamp | null) {
    const previousDate = this.internal;
    this.internal = date;
    this.$emit("change", this.internal);
    this.$emit("dateChanged", this.internal, previousDate);
  }

  setNewInternalDate(date: firestore.Timestamp | null) {
    this.internal = date;
  }

  handle(input: string) {
    this.isPickerOpen = false;

    const timestamp = fromIsoString(input);

    if (timestamp && this.keepHoursAndMinutes && this.internal) {
      const oldDateTime = DateTime.fromMillis(this.internal.toMillis());
      const newDateTime = DateTime.fromMillis(timestamp.toMillis());

      const timestampWithHoursAndMinutes = firestore.Timestamp.fromDate(
        newDateTime
          .set({
            hour: oldDateTime.hour,
            minute: oldDateTime.minute,
            second: 0,
            millisecond: 0,
          })
          .toJSDate()
      );

      this.setNewTimestamp(timestampWithHoursAndMinutes);
    } else {
      this.setNewTimestamp(timestamp);
    }
  }

  get formattedValue() {
    if (this.internal) {
      return this.showWeekday
        ? formatDateWithWeekday(this.internal)
        : formatDateShort(this.internal, this.hideYear);
    } else {
      return "";
    }
  }

  get datePickerValue() {
    const date =
      this.internal?.toDate() ??
      this.initialDatePickerDate?.toDate() ??
      new Date();
    return toDatePickerFormat(date);
  }

  onClearInput() {
    this.setNewTimestamp(null);
  }
}
